import { createElement, VNode } from "preact";
import { MutableRef, useEffect, useRef } from "preact/hooks";

export type StateFunc<S> = (p: S) => VNode<any>;

export type StateViewMap<StateType extends { status: string }> = {
  [S in StateType as S["status"]]: StateFunc<S>;
};

export type RecursiveState<S extends object> = S | (() => RecursiveState<S>);

export function compose<SType extends { status: string }, PType>(
  hook: (p: PType) => RecursiveState<SType>,
  viewMap: StateViewMap<SType>,
): (p: PType) => VNode<any> {
  function withHook(stateHook: () => RecursiveState<SType>): () => VNode<any> {
    function ComposedComponent(): VNode<any> {
      const state = stateHook();

      if (typeof state === "function") {
        const subComponent = withHook(state);
        return createElement(subComponent, {});
      }

      const statusName = state.status as unknown as SType["status"];
      const viewComponent = viewMap[statusName] as unknown as StateFunc<SType>;
      return createElement(viewComponent, state);
    }

    return ComposedComponent;
  }

  return (p: PType) => {
    const h = withHook(() => hook(p));
    return h();
  };
}

export function recursive<PType>(
  hook: (p: PType) => RecursiveState<VNode<any>>,
): (p: PType) => VNode<any> {
  function withHook(stateHook: () => RecursiveState<VNode<any>>): () => VNode<any> {
    function ComposedComponent(): VNode<any> {
      const state = stateHook();

      if (typeof state === "function") {
        const subComponent = withHook(state);
        return createElement(subComponent, {});
      }

      return state;
    }

    return ComposedComponent;
  }

  return (p: PType) => {
    const h = withHook(() => hook(p));
    return h();
  };
}

/**
 * Call `callback` only once.
 *
 * Callback can be a closure with binding to the current caller context. This helper
 * will always take the latest `callback`
 *
 * @param callback
 */
export function useComponentUnload(callback: () => void) {
  /**
   * we use a ref to avoid evaluating the effect function
   * on every render and so the unload is called only once
   */
  const ref = useRef<typeof callback>();
  ref.current = callback;

  useEffect(() => {
    return () => {
      ref.current!();
    };
  }, []);
}

export function composeRef<T>(...fn: ((e: T | null) => void)[]) {
  return (element: T | null) => {
    fn.forEach((handler) => {
      handler(element);
    });
  };
}

export function saveRef<T>(ref: MutableRef<T>) {
  return (element: T | null) => {
    if (element) {
      ref.current = element;
    }
  };
}
/**
 * Show the element when the load ended
 * @param element
 */
export function doAutoFocus<T extends HTMLElement>(
  element: T | null | undefined,
) {
  if (element) {
    setTimeout(() => {
      element.focus({ preventScroll: true });
    }, 100);
  }
}
